import { isOfx, parseCsv, parseOfx, statementFormatOf, type OfxStatement, type StatementFormat } from "@torre/expenses";

// Lectura en el navegador del archivo que exporta el banco (#953). CSV y OFX
// son texto; Excel se lee con exceljs (carga diferida: solo pesa cuando se usa).
// El resultado son filas de celdas que @torre/expenses sabe mapear.

export type StatementFile =
  | { format: "csv" | "xlsx"; rows: unknown[][] }
  | { format: "ofx"; ofx: OfxStatement };

export const isStatementFile = (file: File) => statementFormatOf(file.name) != null;

/** Bytes del archivo (con FileReader donde `Blob.arrayBuffer` no existe). */
function readBuffer(file: Blob): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === "function") return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}

/** Texto de un archivo; si no es UTF-8 válido (bancos que exportan en Latin-1), se relee así. */
async function readText(file: File): Promise<string> {
  const buffer = await readBuffer(file);
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder("windows-1252").decode(buffer);
  }
}

/** Valor de una celda de exceljs a algo que el mapeo entiende. */
export function cellValue(v: any): unknown {
  if (v == null) return "";
  if (v instanceof Date || typeof v === "number" || typeof v === "string") return v;
  if (typeof v === "boolean") return String(v);
  if (typeof v === "object") {
    if ("result" in v) return cellValue(v.result);
    if (Array.isArray(v.richText)) return v.richText.map((r: { text?: string }) => r.text || "").join("");
    if ("text" in v) return String(v.text ?? "");
  }
  return String(v);
}

export async function readXlsxRows(file: File): Promise<unknown[][]> {
  const { default: ExcelJS } = await import("exceljs");
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await readBuffer(file));
  const sheet = wb.worksheets.find((ws) => ws.rowCount > 0) || wb.worksheets[0];
  const rows: unknown[][] = [];
  if (!sheet) return rows;
  sheet.eachRow({ includeEmpty: false }, (row) => {
    const values = Array.isArray(row.values) ? row.values.slice(1) : [];
    rows.push(values.map(cellValue));
  });
  return rows;
}

export async function readStatementFile(file: File): Promise<StatementFile> {
  const format: StatementFormat | null = statementFormatOf(file.name);
  if (format === "xlsx") return { format, rows: await readXlsxRows(file) };
  const text = await readText(file);
  if (format === "ofx" || isOfx(text)) return { format: "ofx", ofx: parseOfx(text) };
  return { format: "csv", rows: parseCsv(text) };
}
